import { MerchantNotificationsEventNamesType } from 'config/requests/merchants';
import { MerchantsShowNotificationsEvent } from 'pages/Merchants/MerchantsShow/MerchantsShowNotifications/MerchantsShowNotificationsEvent/MerchantsShowNotificationsEvent';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { number, required, useShowController } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Typography } from 'shared/mui/Typography';
import { AutocompleteInput, BooleanInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Merchant } from 'types/embedded/merchant';

import { BoxStyled, EventsBoxStyled } from './MerchantsShowNotificationsForm.styled';

export type MerchantsShowNotificationsFormValues = {
  active: boolean;
  chatId?: string;
  email?: string;
  token?: string;
  type: string;
  event: Record<string, boolean>;
  comment?: string;
  provider?: string;
};

type Props = {
  eventNames?: MerchantNotificationsEventNamesType[];
};

export const MerchantsShowNotificationsForm: React.FC<Props> = ({ eventNames }) => {
  const translate = useTypedTranslate();

  const { record: merchant } = useShowController<Merchant>();
  const { watch, setValue } = useFormContext<MerchantsShowNotificationsFormValues>();
  const provider = watch('provider');

  const onChangeProvider = () => {
    eventNames?.forEach((event) => setValue(`event.${event.name}`, false));
  };

  return (
    <CardContent sx={{ width: '640px' }}>
      <BooleanInput
        defaultValue={true}
        label={translate('app.merchantsShowNotifications.active')}
        size="small"
        source="active"
        sx={{ marginBottom: '24px' }}
      />
      <TextInput
        fullWidth
        helperText={false}
        label={translate('app.merchantsShowNotifications.channelId')}
        name="chatId"
        source="chatId"
        sx={{ marginBottom: '24px!important' }}
        validate={[required(), number()]}
        variant="outlined"
      />
      <ReferenceInput
        filter={{ 'merchantAccounts.merchants': merchant?.id }}
        perPage={100}
        reference="providers"
        source="provider"
      >
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          label={translate('app.table.provider')}
          onChange={onChangeProvider}
          optionText="name"
          size="small"
          source="provider"
          variant="outlined"
        />
      </ReferenceInput>
      <TextInput
        fullWidth
        label={translate('app.table.comment')}
        multiline
        name="comment"
        source="comment"
        variant="outlined"
      />
      {eventNames && (
        <EventsBoxStyled>
          <BoxStyled>
            <Typography fontWeight={500}>{translate('app.gatewayRules.rules')}</Typography>
            <MerchantsShowNotificationsEvent
              {...eventNames?.[0]}
              hasProvider={!!provider}
              key={eventNames?.[0].value}
            />
          </BoxStyled>
          <BoxStyled>
            <Typography fontWeight={500}>
              {translate('app.merchantsShowNotifications.orderConversions')}
            </Typography>
            {eventNames?.slice(1, 3).map((props) => (
              <MerchantsShowNotificationsEvent
                {...props}
                hasProvider={!!provider}
                key={props.value}
              />
            ))}
          </BoxStyled>
          <BoxStyled>
            <Typography fontWeight={500}>
              {translate('app.merchantsShowNotifications.rejectionReasons')}
            </Typography>
            {eventNames?.slice(3, 5).map((props) => (
              <MerchantsShowNotificationsEvent
                {...props}
                hasProvider={!!provider}
                key={props.value}
              />
            ))}
          </BoxStyled>
        </EventsBoxStyled>
      )}
    </CardContent>
  );
};
